import React from "react";
import { MOTION } from "../../styles/motionTokens";
import { prefersReducedMotion } from "../../styles/particleField";
import "../styles/CollectorMap.css";

/**
 * MapUtilityControls — privacy above recenter, bottom-anchored above the nav.
 *
 * Two controls, one stack, one owner. They used to be two independently
 * positioned Fabs at `bottom: 72` and `bottom: 124`, which meant the collector
 * preview opened straight over them: the two controls most likely to be wanted
 * after selecting someone were the two the selection covered.
 *
 * Now the stack steps up when a sheet is open. It is the only thing in this
 * layer that moves, and it moves once.
 *
 * That step is 314px, which is a long way to watch something glide, so it is
 * gated on the motion preference. Note the gate has to be here, in JS: the
 * transition is an inline style on an element with no className, so the
 * reduced-motion blocks in CollectorMap.css and MapView.css can neither select
 * it nor, lacking `!important`, outrank it if they could.
 */
const buttonStyle = {
  width: 44,
  height: 44,
  borderRadius: 14,
  background: "rgba(255,255,255,0.96)",
  border: "1px solid rgba(15,23,42,0.08)",
  boxShadow: "0 3px 12px rgba(15,23,42,0.14)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: 0,
};

const MapUtilityControls = ({ bottomPx = 108, onOpenPrivacy, onRecenter, recentering = false, tracking = false }) => {
  // Read at render, never at module scope: the preference can change while the
  // app is open, and a value captured at import time could never notice.
  const reduced = prefersReducedMotion();

  return (
    <div
      data-testid="map-utility-controls"
      className="map-safe-anchor"
      style={{
        position: "absolute",
        right: 12,
        // `bottom` comes from .map-safe-anchor, which adds the safe-area inset
        // the bottom nav grows by. Supply only the offset above the nav band.
        "--map-safe-anchor": `${bottomPx}px`,
        zIndex: 29,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        // The stack still travels — it arrives at the new anchor in one frame
        // instead of sliding there. Pinning it in place would leave the preview
        // sheet sitting on top of the two controls, which is the bug this
        // component exists to fix.
        transition: reduced
          ? "none"
          : `bottom ${MOTION.utilityShiftMs}ms cubic-bezier(0.4, 0, 0.2, 1)`,
      }}
    >
      <button
        type="button"
        aria-label="Location privacy"
        data-testid="privacy-settings"
        onClick={onOpenPrivacy}
        style={{ ...buttonStyle, color: "#334155" }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v5.5c0 4.2-2.9 7.6-7 8.5-4.1-.9-7-4.3-7-8.5V6l7-3z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 12l1.8 1.8L15 10" />
        </svg>
      </button>
      <button
        type="button"
        aria-label={tracking ? "Recenter on my location (tracking active)" : "Recenter on my location"}
        data-testid="my-location"
        onClick={onRecenter}
        style={{
          ...buttonStyle,
          position: "relative",
          color: "#2563eb",
          background: recentering ? "#eef2ff" : buttonStyle.background,
          border: recentering ? "1px solid rgba(37,99,235,0.35)" : buttonStyle.border,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path strokeLinecap="round" d="M12 2v3M12 19v3M2 12h3M19 12h3" />
        </svg>
        {tracking && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#16a34a",
              border: "2px solid #fff",
            }}
          />
        )}
      </button>
    </div>
  );
};

export default MapUtilityControls;
